import { FC, useEffect, useMemo, useRef, useState } from 'react'

export type SvgXYPoint = {
  id?: string
  x: number
  y: number
  name?: string
  isTarget?: boolean
}

type SvgXYChartProps = {
  polygonPoints?: SvgXYPoint[]
  scatterPoints?: SvgXYPoint[]
  xInterval?: number
  yInterval?: number
  xTickPadding?: number
  yTickPadding?: number
  xLabel?: string
  yLabel?: string
  className?: string
  darkMode?: boolean
  polygonFillColor?: string
  scatterPointColor?: string | string[]
  nonTargetScatterPointColor?: string | string[]
  onScatterPointHover?: (point: SvgXYPoint | null) => void
}

const SvgXYChart: FC<SvgXYChartProps> = ({
  polygonPoints = [],
  scatterPoints = [],
  xInterval = 0.001,
  yInterval = 0.001,
  xTickPadding = 1,
  yTickPadding = 1,
  xLabel = 'X',
  yLabel = 'Y',
  className,
  darkMode = false,
  polygonFillColor = 'transparent',
  scatterPointColor,
  nonTargetScatterPointColor,
  onScatterPointHover
}) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const [size, setSize] = useState({ width: 0, height: 0 })
  const [hoveredScatter, setHoveredScatter] = useState<{
    name: string
    x: number
    y: number
    px: number
    py: number
  } | null>(null)

  useEffect(() => {
    const wrapper = wrapperRef.current
    if (!wrapper) {
      return
    }

    let frameId: number | null = null

    const updateSize = () => {
      const nextWidth = wrapper.clientWidth
      const nextHeight = wrapper.clientHeight
      setSize(prev => {
        if (prev.width === nextWidth && prev.height === nextHeight) {
          return prev
        }

        return {
          width: nextWidth,
          height: nextHeight
        }
      })
    }

    updateSize()

    const observer = new ResizeObserver(() => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId)
      }

      frameId = requestAnimationFrame(() => {
        updateSize()
        frameId = null
      })
    })

    observer.observe(wrapper)

    return () => {
      observer.disconnect()
      if (frameId !== null) {
        cancelAnimationFrame(frameId)
      }
    }
  }, [])

  const allPoints = useMemo(
    () => [...polygonPoints, ...scatterPoints],
    [polygonPoints, scatterPoints]
  )

  const buildDomain = (
    values: number[],
    interval: number,
    tickPadding: number
  ): [number, number] => {
    const safeInterval = interval > 0 ? interval : 0.001
    const scale = Math.round(1 / safeInterval)
    const paddingTicks = Math.max(0, Math.floor(tickPadding))

    if (!values.length) {
      const defaultTicks = Math.max(1, paddingTicks)
      return [
        Number((-defaultTicks * safeInterval).toFixed(3)),
        Number((defaultTicks * safeInterval).toFixed(3))
      ]
    }

    const rawMin = Math.min(...values)
    const rawMax = Math.max(...values)

    // Use floor for min and ceil for max so the computed domain always includes
    // all points, even when values are between tick boundaries.
    const minTickIndex = Math.floor(rawMin * scale) - paddingTicks
    const maxTickIndex = Math.ceil(rawMax * scale) + paddingTicks

    if (minTickIndex === maxTickIndex) {
      return [
        Number(((minTickIndex - 1) / scale).toFixed(3)),
        Number(((maxTickIndex + 1) / scale).toFixed(3))
      ]
    }

    return [Number((minTickIndex / scale).toFixed(3)), Number((maxTickIndex / scale).toFixed(3))]
  }

  const xDomain = useMemo(
    () =>
      buildDomain(
        allPoints.map(point => point.x),
        xInterval,
        xTickPadding
      ),
    [allPoints, xInterval, xTickPadding]
  )

  const yDomain = useMemo(
    () =>
      buildDomain(
        allPoints.map(point => point.y),
        yInterval,
        yTickPadding
      ),
    [allPoints, yInterval, yTickPadding]
  )

  const geometry = useMemo(() => {
    const margin = {
      top: 24,
      right: 18,
      bottom: 52,
      left: 74
    }

    const width = Math.max(size.width, 1)
    const height = Math.max(size.height, 1)
    const plotWidth = Math.max(width - margin.left - margin.right, 1)
    const plotHeight = Math.max(height - margin.top - margin.bottom, 1)

    const [xMin, xMax] = xDomain
    const [yMin, yMax] = yDomain
    const xSpan = Math.max(xMax - xMin, Number.EPSILON)
    const ySpan = Math.max(yMax - yMin, Number.EPSILON)

    const xToPx = (value: number) => margin.left + ((value - xMin) / xSpan) * plotWidth
    const yToPx = (value: number) => margin.top + ((yMax - value) / ySpan) * plotHeight

    return {
      width,
      height,
      margin,
      plotWidth,
      plotHeight,
      xToPx,
      yToPx
    }
  }, [size, xDomain, yDomain])

  const polygonPath = useMemo(() => {
    if (polygonPoints.length < 2) {
      return ''
    }

    const commands = polygonPoints.map((point, index) => {
      const px = geometry.xToPx(point.x)
      const py = geometry.yToPx(point.y)
      return `${index === 0 ? 'M' : 'L'} ${px} ${py}`
    })

    return `${commands.join(' ')} Z`
  }, [polygonPoints, geometry])

  const buildTickValues = (min: number, max: number, step: number) => {
    if (step <= 0) {
      return []
    }

    const scale = Math.round(1 / step)
    const start = Math.ceil(min * scale)
    const end = Math.floor(max * scale)
    const values: number[] = []

    for (let i = start; i <= end; i += 1) {
      values.push(Number((i / scale).toFixed(3)))
    }

    return values
  }

  // Rounds a raw step up to the nearest "nice" 1/2/5 x 10^n value.
  const roundToNiceStep = (rawStep: number): number => {
    const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)))
    const residual = rawStep / magnitude
    const niceResidual = residual <= 1 ? 1 : residual <= 2 ? 2 : residual <= 5 ? 5 : 10
    return niceResidual * magnitude
  }

  // Widens a tick step beyond its base interval when the axis span would otherwise
  // pack too many labels into the available pixel length, causing overlap.
  const widenTickStep = (
    span: number,
    interval: number,
    pixelLength: number,
    minPixelsPerTick: number
  ) => {
    if (interval <= 0 || span <= 0) {
      return interval
    }

    const maxTicks = Math.max(2, Math.floor(pixelLength / minPixelsPerTick))
    const baseTickCount = span / interval

    if (baseTickCount <= maxTicks) {
      return interval
    }

    return roundToNiceStep(span / maxTicks)
  }

  const xTickStep = useMemo(
    () => widenTickStep(xDomain[1] - xDomain[0], xInterval, geometry.plotWidth, 48),
    [xDomain, xInterval, geometry.plotWidth]
  )
  const yTickStep = useMemo(
    () => widenTickStep(yDomain[1] - yDomain[0], yInterval, geometry.plotHeight, 28),
    [yDomain, yInterval, geometry.plotHeight]
  )

  const xTicks = useMemo(
    () => buildTickValues(xDomain[0], xDomain[1], xTickStep),
    [xDomain, xTickStep]
  )
  const yTicks = useMemo(
    () => buildTickValues(yDomain[0], yDomain[1], yTickStep),
    [yDomain, yTickStep]
  )

  const palette = darkMode
    ? {
        background: '#111827',
        grid: '#374151',
        axis: '#9ca3af',
        label: '#e5e7eb',
        polygonStroke: '#f87171',
        scatterFill: '#60a5fa'
      }
    : {
        background: '#ffffff',
        grid: '#e5e7eb',
        axis: '#6b7280',
        label: '#111827',
        polygonStroke: '#ef4444',
        scatterFill: '#2563eb'
      }

  // Returns the color for a scatter point by its position among same-type (target vs
  // non-target) points, so array colors stay consistent regardless of other point types.
  const scatterTypeIndices = useMemo(() => {
    let targetCount = 0
    let nonTargetCount = 0
    return scatterPoints.map(point => (point.isTarget ? targetCount++ : nonTargetCount++))
  }, [scatterPoints])

  const getScatterColor = (index: number, isTarget: boolean) => {
    const typeIndex = scatterTypeIndices[index] ?? 0

    if (isTarget) {
      if (Array.isArray(scatterPointColor)) {
        return scatterPointColor[typeIndex % scatterPointColor.length] ?? palette.scatterFill
      }
      return scatterPointColor ?? palette.scatterFill
    }

    if (Array.isArray(nonTargetScatterPointColor)) {
      return nonTargetScatterPointColor[typeIndex % nonTargetScatterPointColor.length] ?? '#16a34a'
    }
    return nonTargetScatterPointColor ?? '#16a34a'
  }

  const scatterTooltip = useMemo(() => {
    if (!hoveredScatter) {
      return null
    }

    const line1 = hoveredScatter.name
    const line2 = `${xLabel}: ${hoveredScatter.x.toFixed(3)}`
    const line3 = `${yLabel}: ${hoveredScatter.y.toFixed(3)}`
    const width = Math.max(line1.length, line2.length, line3.length) * 7 + 12
    const height = 52

    const minX = geometry.margin.left
    const maxX = geometry.margin.left + geometry.plotWidth - width
    const minY = geometry.margin.top

    const x = Math.min(Math.max(hoveredScatter.px + 10, minX), Math.max(maxX, minX))
    const y = Math.max(hoveredScatter.py - height - 8, minY)

    return { x, y, width, height, line1, line2, line3 }
  }, [hoveredScatter, xLabel, yLabel, geometry])

  return (
    <div ref={wrapperRef} className={className ?? 'w-full h-full'}>
      <svg width="100%" height="100%" viewBox={`0 0 ${geometry.width} ${geometry.height}`}>
        <rect
          x={0}
          y={0}
          width={geometry.width}
          height={geometry.height}
          fill={palette.background}
        />

        {xTicks.map(value => {
          const x = geometry.xToPx(value)
          return (
            <g key={`x-${value}`}>
              <line
                x1={x}
                y1={geometry.margin.top}
                x2={x}
                y2={geometry.margin.top + geometry.plotHeight}
                stroke={palette.grid}
                strokeWidth={1}
              />
              <text
                x={x}
                y={geometry.margin.top + geometry.plotHeight + 18}
                textAnchor="middle"
                fill={palette.label}
                fontSize={11}>
                {value.toFixed(3)}
              </text>
            </g>
          )
        })}

        {yTicks.map(value => {
          const y = geometry.yToPx(value)
          return (
            <g key={`y-${value}`}>
              <line
                x1={geometry.margin.left}
                y1={y}
                x2={geometry.margin.left + geometry.plotWidth}
                y2={y}
                stroke={palette.grid}
                strokeWidth={1}
              />
              <text
                x={geometry.margin.left - 8}
                y={y + 4}
                textAnchor="end"
                fill={palette.label}
                fontSize={11}>
                {value.toFixed(3)}
              </text>
            </g>
          )
        })}

        <line
          x1={geometry.margin.left}
          y1={geometry.margin.top + geometry.plotHeight}
          x2={geometry.margin.left + geometry.plotWidth}
          y2={geometry.margin.top + geometry.plotHeight}
          stroke={palette.axis}
          strokeWidth={1.5}
        />
        <line
          x1={geometry.margin.left}
          y1={geometry.margin.top}
          x2={geometry.margin.left}
          y2={geometry.margin.top + geometry.plotHeight}
          stroke={palette.axis}
          strokeWidth={1.5}
        />

        {polygonPath && (
          <path
            d={polygonPath}
            fill={polygonFillColor}
            stroke={palette.polygonStroke}
            strokeWidth={2}
            strokeDasharray="6 4"
          />
        )}

        {scatterPoints.map((point, index) => (
          <circle
            key={`scatter-${index}`}
            cx={geometry.xToPx(point.x)}
            cy={geometry.yToPx(point.y)}
            r={4}
            fill={getScatterColor(index, Boolean(point.isTarget))}
            onMouseEnter={() => {
              setHoveredScatter({
                name: point.name || (point.isTarget ? 'Target' : `Point ${index + 1}`),
                x: point.x,
                y: point.y,
                px: geometry.xToPx(point.x),
                py: geometry.yToPx(point.y)
              })
              onScatterPointHover?.(point)
            }}
            onMouseLeave={() => {
              setHoveredScatter(null)
              onScatterPointHover?.(null)
            }}
          />
        ))}

        {scatterTooltip && (
          <g pointerEvents="none">
            <rect
              x={scatterTooltip.x}
              y={scatterTooltip.y}
              width={scatterTooltip.width}
              height={scatterTooltip.height}
              rx={4}
              fill={darkMode ? '#0f172a' : '#f9fafb'}
              stroke={darkMode ? '#6b7280' : '#d1d5db'}
            />
            <text
              x={scatterTooltip.x + 6}
              y={scatterTooltip.y + 14}
              fill={palette.label}
              fontSize={11}
              fontWeight={600}>
              {scatterTooltip.line1}
            </text>
            <text
              x={scatterTooltip.x + 6}
              y={scatterTooltip.y + 29}
              fill={palette.label}
              fontSize={11}
              fontWeight={600}>
              {scatterTooltip.line2}
            </text>
            <text
              x={scatterTooltip.x + 6}
              y={scatterTooltip.y + 44}
              fill={palette.label}
              fontSize={11}
              fontWeight={600}>
              {scatterTooltip.line3}
            </text>
          </g>
        )}

        <text
          x={geometry.margin.left + geometry.plotWidth / 2}
          y={geometry.height - 14}
          textAnchor="middle"
          fill={palette.label}
          fontSize={12}
          fontWeight={600}>
          {xLabel}
        </text>

        <text
          x={18}
          y={geometry.margin.top + geometry.plotHeight / 2}
          textAnchor="middle"
          transform={`rotate(-90 18 ${geometry.margin.top + geometry.plotHeight / 2})`}
          fill={palette.label}
          fontSize={12}
          fontWeight={600}>
          {yLabel}
        </text>
      </svg>
    </div>
  )
}

export default SvgXYChart
